import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  ChangedChip,
  CurrentValuesBox,
  MAX_REMARK_LENGTH,
  RemarkField,
  SectionLabel,
  ViewHeader,
} from "./EditDialogShared";
import {
  CancelFormActionButton,
  PrimaryFormLoadingActionButton,
} from "../../Features/Table/GenericDraggableTable/TableStyles";
import { useToast } from "../../Hooks/useToast";
import { PrimaryTooltip } from "../StyledComponent";
import { useBatteryInfo, useVehicleInfo } from "../../Pages/DigitalTwinV3/DigitalTwinContext";
import { updateDigitalTwinDetails } from "../../Api/DigitalTwin";
import { getDigitalTwinConfigList } from "../../Api/DigitalTwinListApi";
import { getFilterData } from "../../Api/Api";
import CODE from "../../Static/Constants/StatusCodes";
import {
  MAX_BUS_TYPE_LENGTH,
  buildConfigUpdatePayload,
  buildReviewRows,
  diffConfig,
  findOptionById,
  formatCapacity,
  formatProjectSecondary,
  formatStringTransition,
  formatStrings,
  getBusTypeError,
  getConfigChangeErrorMessage,
  getCurrentStringStructure,
  getFirstDroppedString,
  getStringStructure,
  isContinueEnabled,
  isStringStructureChanged,
  resolveBusType,
  withCurrentOption,
} from "../../Helper/DigitalTwin/StringStructure";
import {
  BUS_TYPE_FILTER_CONFIG,
  PROJECT_PAGE_SIZE,
  PROTOCOL_FILTER_CONFIG,
  buildCurrentConfigFilterConfig,
  buildVehicleConfigData,
} from "../../Helper/DigitalTwin/VehicleConfigOptions";

const STEPS = { EDIT: "edit", REVIEW: "review" };

const LOAD_FAILED_MESSAGE =
  "Could not load the vehicle configuration. Please close and try again.";

// Same input styling as ChangeOriginLocationView, so the selectors in the two
// views are indistinguishable.
const AUTOCOMPLETE_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "14px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    padding: "4px 8px !important",
    "& fieldset": { borderColor: "#E6E6E6", borderWidth: "1px" },
    "&:hover fieldset": { borderColor: "#6D72F6", borderWidth: "1px" },
    "&.Mui-focused fieldset": { borderColor: "#6D72F6", borderWidth: "1px" },
    "& input": { fontSize: "14px", padding: "6px 4px !important" },
    "& input::placeholder": { color: "#A4B2C1", opacity: 1, fontSize: "14px" },
  },
};

const AUTOCOMPLETE_SX = {
  "& .MuiAutocomplete-endAdornment": {
    top: "50% !important",
    transform: "translateY(-50%) !important",
  },
};

const DropdownPaper = (props) => (
  <Paper
    {...props}
    sx={{
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
      "& .MuiAutocomplete-option": { fontSize: "14px" },
    }}
  />
);

const CAPTION_SX = { fontSize: "12px", color: "#6B7280", lineHeight: "18px" };
// A changed capacity or structure reads in amber; strings going away, in red.
const CHANGE_CAPTION_SX = { ...CAPTION_SX, color: "warning.dark", fontWeight: 500 };
const LOSS_CAPTION_SX = { ...CAPTION_SX, color: "#930500", fontWeight: 500 };

const IMPACT_WARNING =
  "This changes the vehicle's rated capacity and battery structure. SOH, energy events " +
  "and other calculations use these values from now on. Past results are not recalculated.";

const REVIEW_CELL_SX = {
  fontSize: "13px",
  padding: "8px 12px",
  borderBottom: "1px solid #F0F0F0",
};

const projectLabel = (project) =>
  project?.configName != null && project.configName !== ""
    ? String(project.configName)
    : project?.configID != null
    ? `Config ${project.configID}`
    : "";

function FieldLabel({ children, changed }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
      <Typography variant="fw400s14c000000" sx={{ lineHeight: "24px", color: "#494949" }}>
        {children}
      </Typography>
      <Typography
        variant="fw400s14c000000"
        sx={{ fontWeight: 700, lineHeight: "24px", color: "#B50500" }}
      >
        &nbsp;*
      </Typography>
      {changed && <ChangedChip />}
    </Box>
  );
}

export default function ChangeVehicleConfigView({ onBack, onSuccess }) {
  const { openNotification } = useToast();
  const { batteryID } = useBatteryInfo();
  const { vehicleSubType: currentVehicleSubType } = useVehicleInfo();

  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [current, setCurrent] = useState(null);
  const [options, setOptions] = useState({ projects: [], protocols: [], busTypes: [] });

  const [step, setStep] = useState(STEPS.EDIT);
  const [draftConfigID, setDraftConfigID] = useState(null);
  const [draftProtocolID, setDraftProtocolID] = useState(null);
  const [busTypeInput, setBusTypeInput] = useState("");
  const [remark, setRemark] = useState("");

  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchOptions = async () => {
      setDataLoading(true);
      setLoadError(null);
      try {
        if (!batteryID) throw new Error(LOAD_FAILED_MESSAGE);

        const [projectsRes, protocolsRes, busTypesRes, currentRes] = await Promise.all([
          getDigitalTwinConfigList({
            pageNumber: 1,
            rowsPerPage: PROJECT_PAGE_SIZE,
            sortBy: "configName",
            orderBy: "asc",
            filters: {},
          }),
          getFilterData(PROTOCOL_FILTER_CONFIG),
          getFilterData(BUS_TYPE_FILTER_CONFIG),
          getFilterData(buildCurrentConfigFilterConfig(batteryID)),
        ]);
        if (cancelled) return;

        const { current: loadedCurrent, options: loadedOptions } = buildVehicleConfigData({
          projectsRes,
          protocolsRes,
          busTypesRes,
          currentRes,
          vehicleSubType: currentVehicleSubType,
        });

        setCurrent(loadedCurrent);
        setOptions(loadedOptions);
        setDraftConfigID(loadedCurrent.configID ?? null);
        setDraftProtocolID(loadedCurrent.protocolID ?? null);
        setBusTypeInput(loadedCurrent.vehicleSubType ?? "");
      } catch (error) {
        if (cancelled) return;
        const msg = getConfigChangeErrorMessage(error);
        setLoadError(msg);
        openNotification({ text: msg, icon: "toast-error" });
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    };

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [batteryID]); // eslint-disable-line react-hooks/exhaustive-deps

  // The vehicle's current project and protocol as option rows, so the
  // preselected value always has a matching option.
  const projectOptions = useMemo(() => {
    if (!current) return [];
    const currentStructure = getCurrentStringStructure(current);
    return withCurrentOption(
      options.projects,
      {
        configID: current.configID,
        configName: current.configName,
        ratedCapacitySystem: current.ratedCapacitySystem,
        buildingBlockType: current.buildingBlockType,
        parallelPacks: currentStructure.stringCount,
        seriesPacks: currentStructure.packsPerString,
      },
      "configID"
    );
  }, [current, options.projects]);

  const protocolOptions = useMemo(() => {
    if (!current) return [];
    return withCurrentOption(
      options.protocols,
      { protocolID: current.protocolID, protocolName: current.protocolName },
      "protocolID"
    );
  }, [current, options.protocols]);

  const selectedProject = findOptionById(projectOptions, "configID", draftConfigID);
  const selectedProtocol = findOptionById(protocolOptions, "protocolID", draftProtocolID);

  const resolvedBusType = resolveBusType(busTypeInput, options.busTypes);
  const busTypeError = getBusTypeError(busTypeInput, current?.vehicleSubType);

  const draft = {
    configID: draftConfigID,
    protocolID: draftProtocolID,
    busType: resolvedBusType.value,
  };
  const changedFields = current ? diffConfig(current, draft) : [];
  const projectChanged = changedFields.includes("configID");
  const protocolChanged = changedFields.includes("protocolID");
  const busTypeChanged = changedFields.includes("vehicleSubType");

  const currentStructure = getCurrentStringStructure(current);
  const targetStructure = getStringStructure(selectedProject);
  const firstDroppedString = projectChanged
    ? getFirstDroppedString(currentStructure, targetStructure)
    : null;
  const structureChanged =
    projectChanged && isStringStructureChanged(currentStructure, targetStructure);
  const capacityChanged =
    projectChanged &&
    formatCapacity(current?.ratedCapacitySystem) !==
      formatCapacity(selectedProject?.ratedCapacitySystem);

  const typedBusType = String(busTypeInput ?? "").trim();
  const busTypeRespelled =
    !!typedBusType && !resolvedBusType.isNew && resolvedBusType.value !== typedBusType;

  const canContinue = isContinueEnabled({
    changedFields,
    remark,
    maxRemarkLength: MAX_REMARK_LENGTH,
    busTypeError,
  });

  const currentRows = [
    { label: "Project", value: current?.configName },
    { label: "Rated Capacity", value: formatCapacity(current?.ratedCapacitySystem) },
    { label: "Strings", value: current ? formatStrings(currentStructure) : null },
    { label: "Protocol", value: current?.protocolName },
    { label: "Bus Type", value: current?.vehicleSubType },
  ];

  const handleContinue = () => {
    if (!canContinue) return;
    setAcknowledged(false);
    setStep(STEPS.REVIEW);
  };

  const handleConfirm = async () => {
    if (isSubmitting || !acknowledged || !canContinue) return;

    setIsSubmitting(true);
    try {
      const response = await updateDigitalTwinDetails(
        batteryID,
        buildConfigUpdatePayload(changedFields, draft, remark)
      );

      if (response?.responseStatus?.code !== CODE.SUCCESS) {
        const err = new Error(response?.responseStatus?.msg);
        err.responseCode = response?.responseStatus?.code;
        throw err;
      }

      await onSuccess("Vehicle Configuration Updated!");
    } catch (error) {
      openNotification({ text: getConfigChangeErrorMessage(error), icon: "toast-error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === STEPS.REVIEW) {
    const reviewRows = buildReviewRows({
      current,
      changedFields,
      project: selectedProject,
      protocol: selectedProtocol,
      busType: resolvedBusType,
    });

    return (
      <Box>
        <ViewHeader
          title="Review Configuration Change"
          onBack={() => !isSubmitting && setStep(STEPS.EDIT)}
        />

        <Box
          sx={{ border: "1px solid #E6E6E6", borderRadius: "8px", overflow: "hidden", mb: 2 }}
        >
          <Table size="small" data-testid="vehicle-config-review-table">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F9F9F9" }}>
                {["Field", "Current", "New"].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{ ...REVIEW_CELL_SX, color: "#6B7280", fontWeight: 600 }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewRows.map((row) => (
                <TableRow
                  key={row.key}
                  data-testid={`vehicle-config-review-row-${row.key}`}
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell sx={{ ...REVIEW_CELL_SX, color: "#494949" }}>{row.label}</TableCell>
                  <TableCell
                    sx={{
                      ...REVIEW_CELL_SX,
                      color: "#9CA3AF",
                      textDecoration: "line-through",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {row.from}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...REVIEW_CELL_SX,
                      color: "#4A4FD6",
                      fontWeight: 600,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {row.to}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <FormControlLabel
          sx={{ alignItems: "flex-start", mx: 0, mb: 2 }}
          control={
            <Checkbox
              data-testid="vehicle-config-acknowledge-checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              size="small"
              sx={{
                p: 0,
                mr: 1,
                mt: "2px",
                color: "#A4B2C1",
                "&.Mui-checked": { color: "#6D72F6" },
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: "13px", color: "#494949", lineHeight: "20px" }}>
              I understand this changes how SOH, energy and related values are calculated
              for this vehicle from now on, and that the change and my remark are recorded.
            </Typography>
          }
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <CancelFormActionButton
            data-testid="vehicle-config-back-btn"
            disabled={isSubmitting}
            onClick={() => setStep(STEPS.EDIT)}
            sx={{ borderRadius: "4px", px: 3 }}
          >
            Back
          </CancelFormActionButton>
          <PrimaryFormLoadingActionButton
            data-testid="vehicle-config-confirm-btn"
            disabled={isSubmitting || !acknowledged}
            onClick={handleConfirm}
            loading={isSubmitting}
            sx={{ borderRadius: "4px", px: 3 }}
          >
            Confirm Change
          </PrimaryFormLoadingActionButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <ViewHeader
        title="Change Project, Protocol & Bus Type"
        onBack={onBack}
        adornment={
          // PrimaryTooltip is the app's standard tooltip (see InfoIconTooltip).
          <PrimaryTooltip title={IMPACT_WARNING} placement="bottom-start">
            <WarningAmberRoundedIcon
              data-testid="vehicle-config-impact-warning"
              aria-label={IMPACT_WARNING}
              sx={{ fontSize: "20px", color: "warning.main" }}
            />
          </PrimaryTooltip>
        }
      />

      {dataLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} sx={{ color: "#6D72F6" }} />
        </Box>
      )}

      {loadError && !dataLoading && (
        <Typography sx={{ fontSize: "14px", color: "#930500", mb: 2 }}>{loadError}</Typography>
      )}

      {!dataLoading && !loadError && current && (
        <>
          {/* Wide two-column layout: what the vehicle has now on the left, the
              form on the right, so the whole change fits without scrolling. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 280px) minmax(0, 1fr)" },
              columnGap: 3,
              rowGap: 2,
              alignItems: "start",
              mb: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <SectionLabel>Current Configuration</SectionLabel>
              <CurrentValuesBox rows={currentRows} columns={1} sx={{ mb: 0 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <SectionLabel>New Configuration</SectionLabel>

              {/* Project and Protocol side by side; Bus Type gets the full row
                  because its new-type warning needs the width. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  columnGap: 2,
                  rowGap: 2,
                }}
              >
                {/* Project */}
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel changed={projectChanged}>Project</FieldLabel>
                  <Autocomplete
                    fullWidth
                    disableClearable
                    options={projectOptions}
                    value={selectedProject}
                    onChange={(_, option) => setDraftConfigID(option?.configID ?? null)}
                    getOptionLabel={projectLabel}
                    getOptionKey={(option) => option?.configID}
                    isOptionEqualToValue={(option, value) =>
                      Number(option?.configID) === Number(value?.configID)
                    }
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={key} {...rest}>
                          <Box component="span">{projectLabel(option)}</Box>
                          <Box component="span" sx={{ color: "#8D8D8D", ml: 0.75, fontSize: "12px" }}>
                            · {formatProjectSecondary(option)}
                          </Box>
                        </li>
                      );
                    }}
                    sx={AUTOCOMPLETE_SX}
                    PaperComponent={DropdownPaper}
                    slotProps={{ listbox: { sx: { fontSize: "14px" } } }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search or select Project"
                        sx={AUTOCOMPLETE_INPUT_SX}
                        inputProps={{ ...params.inputProps, "data-testid": "vehicle-config-project-field" }}
                      />
                    )}
                  />
                </Box>

                {/* Protocol */}
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel changed={protocolChanged}>Protocol</FieldLabel>
                  <Autocomplete
                    fullWidth
                    disableClearable
                    options={protocolOptions}
                    value={selectedProtocol}
                    onChange={(_, option) => setDraftProtocolID(option?.protocolID ?? null)}
                    getOptionLabel={(option) => (option?.protocolName ? String(option.protocolName) : "")}
                    getOptionKey={(option) => option?.protocolID}
                    isOptionEqualToValue={(option, value) =>
                      Number(option?.protocolID) === Number(value?.protocolID)
                    }
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={key} {...rest}>
                          {option?.protocolName ?? `Protocol ${option?.protocolID}`}
                        </li>
                      );
                    }}
                    sx={AUTOCOMPLETE_SX}
                    PaperComponent={DropdownPaper}
                    slotProps={{ listbox: { sx: { fontSize: "14px" } } }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search or select Protocol"
                        sx={AUTOCOMPLETE_INPUT_SX}
                        inputProps={{ ...params.inputProps, "data-testid": "vehicle-config-protocol-field" }}
                      />
                    )}
                  />
                </Box>
              </Box>

              {projectChanged && (
                <Box sx={{ mt: 1 }} data-testid="vehicle-config-project-impact">
                  <Typography sx={capacityChanged ? CHANGE_CAPTION_SX : CAPTION_SX}>
                    Rated capacity {formatCapacity(current.ratedCapacitySystem) ?? "N/A"} →{" "}
                    {formatCapacity(selectedProject?.ratedCapacitySystem) ?? "N/A"}
                  </Typography>
                  <Typography
                    sx={
                      firstDroppedString !== null
                        ? LOSS_CAPTION_SX
                        : structureChanged
                        ? CHANGE_CAPTION_SX
                        : CAPTION_SX
                    }
                  >
                    {formatStringTransition(currentStructure, targetStructure)}
                  </Typography>
                </Box>
              )}

              {/* Bus Type */}
              <Box sx={{ mt: 1.5, mb: 1.5 }}>
                <FieldLabel changed={busTypeChanged}>Bus Type</FieldLabel>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={options.busTypes}
                  value={busTypeInput}
                  inputValue={busTypeInput}
                  onInputChange={(_, value) => setBusTypeInput(value ?? "")}
                  onChange={(_, value) => setBusTypeInput(value ?? "")}
                  sx={AUTOCOMPLETE_SX}
                  PaperComponent={DropdownPaper}
                  slotProps={{ listbox: { sx: { fontSize: "14px" } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search, select or type a Bus Type"
                      sx={AUTOCOMPLETE_INPUT_SX}
                      error={!!busTypeError}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: MAX_BUS_TYPE_LENGTH,
                        "data-testid": "vehicle-config-bus-type-field",
                      }}
                    />
                  )}
                />
                {busTypeError && (
                  <Typography sx={{ fontSize: "10px", color: "#930500", mt: 0.5 }}>
                    {busTypeError}
                  </Typography>
                )}
                {busTypeChanged && busTypeRespelled && (
                  <Typography sx={{ ...CAPTION_SX, mt: 0.75 }}>
                    Will be saved as &quot;{resolvedBusType.value}&quot;, the existing spelling.
                  </Typography>
                )}
                {busTypeChanged && resolvedBusType.isNew && (
                  <Typography sx={{ ...CHANGE_CAPTION_SX, mt: 0.75 }}>
                    New bus type, not used by any vehicle yet. Check the spelling.
                  </Typography>
                )}
              </Box>

              <RemarkField
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                testId="vehicle-config-remark-field"
                minRows={2}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PrimaryFormLoadingActionButton
              data-testid="vehicle-config-continue-btn"
              disabled={!canContinue}
              onClick={handleContinue}
              sx={{ borderRadius: "4px", px: 3 }}
            >
              Continue
            </PrimaryFormLoadingActionButton>
          </Box>
        </>
      )}
    </Box>
  );
}
